import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../services/authService';
import { User, LoginRequest, ResetPasswordRequest } from '../types/index';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<boolean>;
  logout: () => Promise<void>;
  hasRole: (role: string) => boolean;
  hasPermission: (permission: string) => boolean;
  requestPasswordReset: (email: string) => Promise<boolean>;
  resetPassword: (data: ResetPasswordRequest) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (error) {
        console.error('Failed to parse user data:', error);
        localStorage.removeItem('user');
      }
    }
    setIsLoading(false);
  }, []);
  
  // Update the login function in AuthProvider
  const login = async (credentials: { username: string; password: string }): Promise<boolean> => {
    setIsLoading(true);
    try {
      const data = await authService.login(credentials.username, credentials.password);
      setUser(data.user);
      return true;
    } catch (error) {
      console.error('Login error:', error);
      
      // Clear any existing user data on error
      setUser(null);
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      
      return false; // Return false for failure
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      await authService.logout();
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const requestPasswordReset = async (email: string): Promise<boolean> => {
    try {
      const response = await authService.requestPasswordReset(email);
      return response.success;
    } catch (error) {
      console.error('Password reset request error:', error);
      throw error;
    }
  };

  const resetPassword = async (data: ResetPasswordRequest): Promise<boolean> => {
    try {
      const response = await authService.resetPassword(data as any);
      return response.success;
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  };

  const hasRole = (role: string): boolean => {
    return user?.role.role_name === role;
  };

  const hasPermission = (permission: string): boolean => {
    if (!user) return false;
    
    const roleName = user.role?.role_name;
    
    switch (permission) {
      case 'create_record':
        return roleName  === 'DATA_ENTRY_OFFICER' || roleName  === 'DATA_MANAGER';
      
      case 'edit_record':
        return roleName  === 'DATA_ENTRY_OFFICER' || roleName  === 'DATA_MANAGER';
      
      case 'delete_record':
        return roleName  === 'DATA_ENTRY_OFFICER' || roleName  === 'DATA_MANAGER';
      
      case 'verify_record':
        return roleName  === 'DATA_MANAGER';
      
      case 'export_data':
        return roleName  === 'DATA_MANAGER';
      
      case 'view_audit_logs':
        return roleName  === 'DATA_MANAGER';
      
      case 'view_all_records':
        return roleName  === 'DATA_MANAGER' || roleName  === 'LEAD_MANAGER';
      
      default:
        return false;
    }
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    hasRole,
    hasPermission,
    requestPasswordReset,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};